import axios, { AxiosError, AxiosInstance } from 'axios'

import { API_URL } from '../config/api'
// ============================================================================
// TYPES - Social API tipleri
// ============================================================================

export interface Post {
  id: number
  user_id: number
  agent_id?: number
  content: string
  code_snippet?: string
  code_language?: string
  post_type: 'text' | 'code' | 'question' | 'showcase' | 'research' | 'skill_discussion'
  skill_tags: string[]
  difficulty_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  collaboration_request: boolean
  quality_score?: number
  like_count: number
  comment_count: number
  share_count: number
  view_count: number
  agent_contribution_count: number
  created_at: string
  updated_at: string
  aicloud_skill_id?: number
  skill_name?: string
  skill_color?: string
  skill_category?: string
  skill_description?: string
  skill_rating?: number
  skill_version?: string
  skill_downloads?: number
  user?: {
    id: number
    username: string
    display_name: string
    avatar_url?: string
    badge?: string
    level?: number
  }
  agent?: {
    id: number
    name: string
    agent_type: 'expert' | 'baby' | 'mentor' | 'master'
    avatar_url?: string
    llm_type?: string   // 'runai_cloud' | 'cloud_api' | 'local_llm' | 'petals'
    user_type?: string  // 'anne' | 'baba' | 'uzman' | 'bebek'
  }
  is_liked?: boolean
  is_bookmarked?: boolean
}

export interface Comment {
  id: number
  post_id: number
  user_id?: number
  agent_id?: number
  parent_comment_id?: number
  content: string
  agent_confidence?: number
  agent_reasoning?: string
  agent_sources?: string[]
  helpful_count: number
  like_count: number
  created_at: string
  updated_at: string
  user?: {
    id: number
    username: string
    display_name: string
    avatar_url?: string
  }
  agent?: {
    id: number
    name: string
    agent_type: 'expert' | 'baby' | 'mentor' | 'master'
    avatar_url?: string
  }
  replies?: Comment[]
}

export interface TrendingTopic {
  tag: string
  post_count: number
  trend_score: number
}

export interface LiveActivity {
  id: number
  user_id?: number
  agent_id?: number
  activity_type: 'post_created' | 'comment_added' | 'like_given' | 'agent_contributed'
  target_type: 'post' | 'comment'
  target_id: number
  created_at: string
  user?: {
    id: number
    username: string
    display_name: string
    avatar_url?: string
  }
  agent?: {
    id: number
    name: string
    agent_type: 'expert' | 'baby' | 'mentor' | 'master'
  }
  post?: {
    id: number
    content: string
  }
}

export interface PlatformStats {
  total_posts: number
  total_users: number
  total_agents: number
  total_comments: number
  active_users_today: number
}

export interface PostFilters {
  page?: number
  per_page?: number
  offset?: number
  category?: string
  skill_tags?: string[]
  post_type?: string
  difficulty_level?: string
  sort_by?: 'recent' | 'trending' | 'top'
  hashtag?: string
  skill_id?: number
  user_id?: number
}

export interface CreatePostRequest {
  content: string
  code_snippet?: string
  code_language?: string
  post_type: 'text' | 'code' | 'question' | 'showcase' | 'research'
  skill_tags: string[]
  difficulty_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  collaboration_request?: boolean
}

export interface CreateCommentRequest {
  content: string
  parent_comment_id?: number
}

export interface LikeRequest {
  target_type: 'post' | 'comment'
  target_id: number
}

export interface BookmarkRequest {
  post_id: number
  collection_name?: string
}

export interface AgentContribution {
  id: number
  agent_id: number
  post_id: number
  comment_id: number
  confidence: number
  reasoning: string
  sources: string[]
  created_at: string
  agent: {
    id: number
    name: string
    agent_type: 'expert' | 'baby' | 'mentor' | 'master'
    avatar_url?: string
  }
  comment: Comment
}

// ============================================================================
// API ERROR CLASS
// ============================================================================

export class APIError extends Error {
  status: number
  code?: string
  details?: Record<string, any>

  constructor(message: string, status: number, code?: string, details?: Record<string, any>) {
    super(message)
    this.name = 'APIError'
    this.status = status
    this.code = code
    this.details = details
  }
}

// ============================================================================
// AXIOS INSTANCE - Social API
// ============================================================================

const socialApi: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/social`,
  timeout: 30000, // 30 saniye
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Authorization header ekle
socialApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') ?? localStorage.getItem('session_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    console.error('Request interceptor hatasi:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - Hata yonetimi (retry yok - network hatasi varsa direkt fail)
socialApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // Sadece 5xx sunucu hatalari icin 1 kez retry
    if (!originalRequest._retry && error.response && error.response.status >= 500) {
      originalRequest._retry = true
      await new Promise((resolve) => setTimeout(resolve, 1000))
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return socialApi(originalRequest)
    }

    // Hatayi parse et ve throw et
    throw parseError(error)
  }
)

// Hatayi parse et
function parseError(error: AxiosError): APIError {
  if (error.response) {
    const data = error.response.data as Record<string, any>
    return new APIError(
      (data?.message as string | undefined) || (data?.detail as string | undefined) || 'An error occurred',
      error.response.status,
      data?.code as string | undefined,
      data?.details as Record<string, any> | undefined
    )
  } else if (error.request) {
    return new APIError(
      'Cannot reach server. Please check your internet connection.',
      0,
      'NETWORK_ERROR'
    )
  } else {
    return new APIError(error.message || 'An unexpected error occurred', 0, 'REQUEST_ERROR')
  }
}

// ============================================================================
// POST ENDPOINTS - Gonderi islemleri
// ============================================================================

/**
 * Gonderi listesini getir (filtreleme ve sayfalama ile)
 * GET /api/social/posts
 */
export async function getPosts(params: PostFilters = {}): Promise<{ posts: Post[]; total: number }> {
  // per_page → limit donusumu (backend limit bekliyor)
  const { per_page, ...rest } = params
  const queryParams = { ...rest, ...(per_page ? { limit: per_page } : {}) }
  const { data } = await socialApi.get<{ posts: Post[]; total: number; count?: number }>('/posts', { params: queryParams })
  return { posts: data.posts, total: data.total ?? data.count ?? 0 }
}

/**
 * Tek bir gonderiyi getir
 * GET /api/social/posts/:id
 */
export async function getPost(postId: number, userId?: number): Promise<Post> {
  const { data } = await socialApi.get<Post>(`/posts/${postId}`, {
    params: userId ? { user_id: userId } : {}
  })
  return data
}

/**
 * Yeni gonderi olustur
 * POST /api/social/posts?user_id=X
 */
export async function createPost(postData: CreatePostRequest, userId: number): Promise<Post> {
  const { data } = await socialApi.post<Post>('/posts', postData, { params: { user_id: userId } })
  return data
}

/**
 * Gonderiyi guncelle
 * PUT /api/social/posts/:id?user_id=X
 */
export async function updatePost(postId: number, postData: Partial<CreatePostRequest>, userId: number): Promise<Post> {
  const { data } = await socialApi.put<Post>(`/posts/${postId}`, postData, { params: { user_id: userId } })
  return data
}

/**
 * Gonderiyi sil
 * DELETE /api/social/posts/:id?user_id=X
 */
export async function deletePost(postId: number, userId: number): Promise<void> {
  await socialApi.delete(`/posts/${postId}`, { params: { user_id: userId } })
}

/**
 * Takip edilen kullanicilarin gonderilerini getir (Timeline)
 * GET /api/social/timeline
 */
export async function getTimeline(userId: number, params: PostFilters = {}): Promise<{ posts: Post[]; total: number }> {
  const { data } = await socialApi.get<{ posts: Post[]; total: number }>('/timeline', {
    params: { ...params, user_id: userId },
  })
  return data
}

/**
 * Trend olan gonderileri getir
 * GET /api/social/trending
 */
export async function getTrending(category?: string, limit: number = 20): Promise<Post[]> {
  const { data } = await socialApi.get<{ posts: Post[] }>('/trending', {
    params: { category, limit },
  })
  return data.posts
}

// ============================================================================
// COMMENT ENDPOINTS - Yorum islemleri
// ============================================================================

/**
 * Gonderi yorumlarini getir
 * GET /api/social/posts/:postId/comments
 */
export async function getComments(postId: number): Promise<Comment[]> {
  const { data } = await socialApi.get<{ comments: Comment[] }>(`/posts/${postId}/comments`)
  return data.comments
}

/**
 * Yorum ekle
 * POST /api/social/posts/:postId/comments
 */
export async function createComment(postId: number, commentData: CreateCommentRequest, userId: number): Promise<Comment> {
  const { data } = await socialApi.post<Comment>(`/posts/${postId}/comments?user_id=${userId}`, commentData)
  return data
}

// ============================================================================
// ENGAGEMENT ENDPOINTS - Etkilesim islemleri
// ============================================================================

/**
 * Begeni ekle/kaldir (toggle)
 * POST /api/social/like?user_id=X&target_type=Y&target_id=Z
 */
export async function toggleLike(userId: number, targetType: 'post' | 'comment', targetId: number): Promise<{ is_liked: boolean; like_count: number }> {
  const { data } = await socialApi.post<{ liked: boolean; like_count?: number; success: boolean }>('/like', null, {
    params: {
      user_id: userId,
      target_type: targetType,
      target_id: targetId,
    },
  })
  return { is_liked: data.liked, like_count: data.like_count ?? -1 }
}

/**
 * Post goruntuleme kaydet
 * POST /api/social/posts/{post_id}/view
 */
export async function trackView(postId: number, userId?: number): Promise<{ success: boolean; view_count: number }> {
  const { data } = await socialApi.post<{ success: boolean; view_count: number }>(`/posts/${postId}/view`, null, {
    params: userId ? { user_id: userId } : undefined
  })
  return data
}

/**
 * Post paylasim sayisini artir
 * POST /api/social/posts/{post_id}/share
 */
export async function trackShare(postId: number): Promise<{ success: boolean; share_count: number }> {
  try {
    const { data } = await socialApi.post<{ success: boolean; share_count: number }>(`/posts/${postId}/share`)
    return data
  } catch {
    return { success: false, share_count: 0 }
  }
}

/**
 * Takip et/takibi birak (toggle)
 * POST /api/social/follow?follower_id=X&following_id=Y
 */
export async function toggleFollow(followerId: number, followingId: number): Promise<{ is_following: boolean }> {
  const { data } = await socialApi.post<{ following: boolean; success: boolean }>('/follow', null, {
    params: { follower_id: followerId, following_id: followingId },
  })
  return { is_following: data.following }
}

/**
 * Yer imi ekle/kaldir (toggle)
 * POST /api/social/bookmark?user_id=X&post_id=Y
 */
export async function toggleBookmark(userId: number, postId: number, collectionName?: string): Promise<{ is_bookmarked: boolean }> {
  const { data } = await socialApi.post<{ bookmarked: boolean; success: boolean }>('/bookmark', null, {
    params: { user_id: userId, post_id: postId, collection_name: collectionName },
  })
  return { is_bookmarked: data.bookmarked }
}

/**
 * Kullanicinin yer imlerini getir
 * GET /api/social/bookmarks
 */
export async function getBookmarks(userId: number): Promise<Post[]> {
  const { data } = await socialApi.get<{ posts: Post[] }>('/bookmarks', {
    params: { user_id: userId },
  })
  return data.posts
}

// ============================================================================
// AGENT ENDPOINTS - Agent islemleri
// ============================================================================

/**
 * Trend olan agentlari getir
 * GET /api/social/trending/agents
 */
export async function getTrendingAgents(limit: number = 7): Promise<any[]> {
  const { data } = await socialApi.get<{ agents: any[] }>('/trending/agents', {
    params: { limit },
  })
  return data.agents
}

/**
 * Trend olan hashtag'leri getir
 * GET /api/social/trending/hashtags
 */
export async function getTrendingHashtags(limit: number = 5): Promise<any[]> {
  const { data } = await socialApi.get<{ hashtags: any[] }>('/trending/hashtags', {
    params: { limit },
  })
  return data.hashtags
}

/**
 * Kullanici istatistiklerini getir
 * GET /api/social/stats/user/:userId
 */
export async function getUserStats(userId: number): Promise<{
  following_count: number
  saved_count: number
  research_count: number
  project_count: number
}> {
  const { data } = await socialApi.get(`/stats/user/${userId}`)
  return data
}

/**
 * Gonderiye yapilan agent katkilarini getir
 * GET /api/social/posts/:postId/agent-contributions
 */
export async function getAgentContributions(postId: number): Promise<AgentContribution[]> {
  const { data } = await socialApi.get<{ contributions: AgentContribution[] }>(`/posts/${postId}/agent-contributions`)
  return data.contributions
}

// ============================================================================
// ACTIVITY ENDPOINTS - Aktivite islemleri
// ============================================================================

/**
 * Canli aktivite akisini getir
 * GET /api/social/activity/live
 */
export async function getLiveActivity(limit: number = 8): Promise<LiveActivity[]> {
  const { data } = await socialApi.get<{ activities: LiveActivity[] }>('/activity/live', {
    params: { limit },
  })
  return data.activities
}

/**
 * Platform istatistiklerini getir
 * GET /api/social/stats
 */
export async function getStats(): Promise<PlatformStats> {
  const { data } = await socialApi.get<PlatformStats>('/stats')
  return data
}

export default socialApi
